import { useState, useEffect } from 'react';
import { projectsApi } from '../../../services/api';
import { api } from '../../../services/api';
import type { MermaidDiagram, MermaidDiagramType } from './types';
import { MERMAID_TEMPLATES } from '../../../config/mermaid-templates';
import { 
  Button, 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogFooter, 
  DialogTitle, 
  DialogDescription,
  Input,
  Label,
} from '@/components/ui';
import { logger } from '../../../utils/logger';

interface FlowDiagramsTabProps {
  projectId: string;
  onOpenFlowDiagram?: (diagram: {
    id: string;
    diagramId: string;
    documentNumber?: string;
    projectId: string;
    title: string;
    mermaidType: MermaidDiagramType;
    svgUrl?: string;
  }) => void;
  flowDiagrams: MermaidDiagram[];
  setFlowDiagrams: (diagrams: MermaidDiagram[]) => void;
}

export function FlowDiagramsTab({ projectId, onOpenFlowDiagram, flowDiagrams, setFlowDiagrams }: FlowDiagramsTabProps) {
  const [loadingFlowDiagrams, setLoadingFlowDiagrams] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  
  const [showNewFlowModal, setShowNewFlowModal] = useState(false);
  const [showDeleteFlowModal, setShowDeleteFlowModal] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<MermaidDiagram | null>(null);
  const [newFlowTitle, setNewFlowTitle] = useState('');
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [isDeletingFlow, setIsDeletingFlow] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    const load = async () => {
      setLoadingFlowDiagrams(true);
      try {
        const projectFlowDiagrams = await projectsApi.listMermaidDiagrams(projectId);
        setFlowDiagrams(projectFlowDiagrams);
        setHasLoaded(true);
      } catch (err) {
        logger.error('Failed to load flow diagrams:', err);
      } finally {
        setLoadingFlowDiagrams(false);
      }
    };
    load();
  }, [projectId, hasLoaded, setFlowDiagrams]);

  const handleFlowDiagramClick = (diagram: MermaidDiagram) => {
    if (onOpenFlowDiagram) {
      onOpenFlowDiagram({
        id: diagram.diagramId,
        diagramId: diagram.diagramId,
        projectId,
        title: diagram.documentTitle,
        mermaidType: diagram.mermaidType,
        svgUrl: diagram.svgUrl,
      });
    }
  };

  const handleCreateFlowDiagram = async () => {
    if (!newFlowTitle.trim()) return;
    
    setIsCreatingFlow(true);
    try {
      const defaultSource = MERMAID_TEMPLATES.flowchart;
      const timestamp = Date.now();
      const documentNumber = `MER-${projectId.substring(0, 4).toUpperCase()}-${timestamp.toString(36).toUpperCase()}`;
      
      const documentPayload = {
        documentNumber,
        documentTitle: newFlowTitle.trim(),
        documentType: 'mermaid_diagram',
        title: newFlowTitle.trim(),
        body: [
          {
            id: 'sec-diagram',
            type: 'section',
            number: '1',
            title: 'Diagram',
            blocks: [
              {
                id: 'blk-mermaid',
                type: 'vector_graphic',
                content: {
                  format: 'mermaid',
                  content: defaultSource,
                  caption: null,
                },
              },
            ],
          },
        ],
        metadata: {
          mermaidType: 'flowchart',
          title: newFlowTitle.trim(),
        },
      };
      
      const response = await api.post(`/projects/${projectId}/documents`, documentPayload);
      const newDocument = response.data.data;
      
      const newDiagram = {
        diagramId: newDocument.documentNumber,
        documentNumber: newDocument.documentNumber,
        documentTitle: newFlowTitle.trim(),
        mermaidType: 'flowchart' as const,
        projectId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as MermaidDiagram;
      
      setFlowDiagrams([...flowDiagrams, newDiagram]);
      setShowNewFlowModal(false);
      setNewFlowTitle('');
      
      if (onOpenFlowDiagram) {
        onOpenFlowDiagram({
          id: newDocument.documentNumber,
          diagramId: newDocument.documentNumber,
          documentNumber: newDocument.documentNumber,
          projectId,
          title: newFlowTitle.trim(),
          mermaidType: 'flowchart',
        });
      }
    } catch (err) {
      logger.error('Failed to create flow diagram:', err);
    } finally {
      setIsCreatingFlow(false);
    }
  };

  const handleDeleteFlowDiagram = async () => {
    if (!flowToDelete) return;
    
    setIsDeletingFlow(true);
    try {
      const documentNumber = flowToDelete.documentNumber || flowToDelete.diagramId;
      await api.delete(`/projects/${projectId}/documents/${documentNumber}`);
      setFlowDiagrams(flowDiagrams.filter(d => d.diagramId !== flowToDelete.diagramId));
      setShowDeleteFlowModal(false);
      setFlowToDelete(null);
    } catch (err) {
      logger.error('Failed to delete flow diagram:', err);
    } finally {
      setIsDeletingFlow(false);
    }
  };

  const openDeleteFlowModal = (diagram: MermaidDiagram, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlowToDelete(diagram);
    setShowDeleteFlowModal(true);
  };

  return (
    <div className="space-y-4">
      {/* Header with New Flow Diagram button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          AI-generated flow diagrams, sequences, and visualizations
        </p>
        <Button
          onClick={() => setShowNewFlowModal(true)}
          variant="secondary"
          size="sm"
        >
          + New Flow Diagram
        </Button>
      </div>
      
      {/* Flow Diagram list */}
      {loadingFlowDiagrams ? (
        <div className="text-center py-8">
          <div className="animate-spin text-2xl">⏳</div>
        </div>
      ) : flowDiagrams.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <div className="text-4xl mb-3">📊</div>
          <p>No flow diagrams yet</p>
          <p className="text-sm mt-1">
            Create a new flow diagram or ask the AI to generate one
          </p>
          <Button
            onClick={() => setShowNewFlowModal(true)}
            variant="secondary"
            size="sm"
            className="mt-4"
          >
            Create First Flow Diagram
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {flowDiagrams.map(diagram => (
            <div
              key={diagram.diagramId}
              className="p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => handleFlowDiagramClick(diagram)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    <span className="text-sm font-medium text-foreground">
                      {diagram.documentTitle}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {diagram.diagramId}
                  </p>
                  {diagram.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {diagram.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {diagram.status !== 'Released' && (
                    <Button
                      onClick={(e) => openDeleteFlowModal(diagram, e)}
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      title="Delete"
                    >
                      <span className="text-sm">🗑️</span>
                    </Button>
                  )}
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      diagram.mermaidType === 'flowchart' ? 'bg-info-light text-info' :
                      diagram.mermaidType === 'sequence' ? 'bg-secondary text-secondary-foreground' :
                      diagram.mermaidType === 'state' ? 'bg-success-light text-success' :
                      diagram.mermaidType === 'er' ? 'bg-warning-light text-warning' :
                      'bg-info-light text-info'
                    }`}>
                      {diagram.mermaidType}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      diagram.status === 'Released'
                        ? 'bg-success-light text-success'
                        : 'bg-warning-light text-warning'
                    }`}>
                      {diagram.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Flow Diagram Modal */}
      <Dialog open={showNewFlowModal} onOpenChange={(open) => {
        if (!open) {
          setShowNewFlowModal(false);
          setNewFlowTitle('');
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>New Flow Diagram</DialogTitle>
            <DialogDescription>
              Create a new Mermaid-based flow diagram for this project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 flex-1 overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="flow-title">Diagram Title *</Label>
              <Input
                id="flow-title"
                value={newFlowTitle}
                onChange={(e) => setNewFlowTitle(e.target.value)}
                placeholder="e.g., Software Architecture Overview"
                autoFocus
              />
            </div>
            <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-medium text-foreground mb-1">Diagram Type Selection</p>
                  <p>Your diagram starts with a basic flowchart template. Use the <strong className="text-foreground">Templates</strong> button in the editor toolbar to change the diagram type (flowchart, sequence, class, etc.) or let AI generate the appropriate structure for you.</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowNewFlowModal(false);
                setNewFlowTitle('');
              }}
              variant="outline"
              disabled={isCreatingFlow}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFlowDiagram}
              disabled={!newFlowTitle.trim() || isCreatingFlow}
            >
              {isCreatingFlow ? 'Creating...' : 'Create Diagram'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Flow Diagram Modal */}
      <Dialog open={showDeleteFlowModal && !!flowToDelete} onOpenChange={(open) => {
        if (!open) {
          setShowDeleteFlowModal(false);
          setFlowToDelete(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Flow Diagram</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this flow diagram? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {flowToDelete && (
            <div className="py-4">
              <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-2">
                <div>
                  <strong>Diagram:</strong> {flowToDelete.documentTitle}
                </div>
                <div>
                  <strong>ID:</strong> {flowToDelete.diagramId}
                </div>
                <div>
                  <strong>Type:</strong> {flowToDelete.mermaidType}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteFlowModal(false);
                setFlowToDelete(null);
              }}
              variant="outline"
              disabled={isDeletingFlow}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteFlowDiagram}
              disabled={isDeletingFlow}
              variant="destructive"
            >
              {isDeletingFlow ? 'Deleting...' : 'Delete Diagram'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
